import {
  DynamicForm,
  type DynamicFormFieldType,
  type DynamicFormRef,
  type JsonData,
  type JsonDataItem,
  type JsonDataWrapper,
  type OdinDataRetrieval,
  type OdinDataRetrievalOptions,
  OdinDataSender,
  type OdinKey,
} from '@myosh/odin-components';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useGetLocalesQuery } from '../../../redux/services/api';
import {
  type FormBuilderField,
  selectFieldGroups,
  selectFormData,
  selectSelectedField,
  setSelectedField,
  updateFormFieldData,
} from '../../../redux/slices/form-builder-slice';
import CloseActiveItemButton from '../../common/close-active-item-button';
import { getAllFormFields } from '../fields/ai-field/ai-field-form.util';
import {
  getFieldSpecificData,
  getFieldValue,
  getFormBuilderFieldOptionsData,
} from '../util/form-builder-details.utils';
import useFormBuilderDetailsSettings from './use-form-builder-details-settings';

function FormBuilderFieldDetails() {
  const selectedFormData = useAppSelector(selectFormData);
  const selectedField = useAppSelector(selectSelectedField);
  const fieldGroups = useAppSelector(selectFieldGroups);

  const dispatch = useAppDispatch();

  const formSettings = useFormBuilderDetailsSettings();
  const [formData, setFormData] = useState<JsonDataItem>({});

  const formIdRef = useRef(crypto.randomUUID());
  const dynamicFormRef = useRef<DynamicFormRef>(null);

  const selectedFieldRef = useRef(selectedField);
  selectedFieldRef.current = selectedField;

  const { data: locales } = useGetLocalesQuery();
  const localeIdsRef = useRef(locales);
  localeIdsRef.current = locales;

  const loadFieldBasicData = useCallback((field: FormBuilderField) => {
    return {
      fieldCaption: field.fieldCaption?.translations[0]?.value,
      help: field.help?.translations[0]?.value,
      hint: field.hint?.translations[0]?.value,
      fieldType: { text: field.fieldType, value: field.fieldType },
      required: field.required,
      lockDown: field.lockDown,
    };
  }, []);

  const allFormFields = useMemo(() => selectedFormData && getAllFormFields(selectedFormData), [selectedFormData]);

  useEffect(() => {
    if (selectedField) {
      dynamicFormRef.current?.resetFormState({});
      const basicData = loadFieldBasicData(selectedField);
      getFieldSpecificData(selectedField, fieldGroups, allFormFields).then((fieldSpecificData) => {
        setFormData({
          ...basicData,
          ...fieldSpecificData,
        });
      });
    }
  }, [selectedField?.id, selectedField?.uuid]);

  const handleChange = useMemo(
    () =>
      debounce(async (_fieldId: OdinKey, fieldName: string, _fieldType: DynamicFormFieldType, fieldValue: unknown) => {
        const errors = dynamicFormRef.current?.getFieldErrors() ?? {};
        if (errors[fieldName] || !selectedFieldRef.current || !localeIdsRef.current) {
          return;
        }

        const value = getFieldValue(fieldName, fieldValue, localeIdsRef.current);

        dispatch(
          updateFormFieldData({
            id: selectedFieldRef.current.id,
            groupId: selectedFieldRef.current.groupId,
            uuid: selectedFieldRef.current.uuid,
            [fieldName]: value,
          })
        );
      }, 400),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      handleChange.cancel();
    };
  }, [handleChange]);

  const onCloseFieldDetailsClick = useCallback(() => {
    dispatch(setSelectedField(undefined));
  }, [dispatch]);

  const dataRetrieval: OdinDataRetrieval = useMemo(() => {
    return {
      getData: (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options && selectedFormData) {
          getFormBuilderFieldOptionsData(subscriber, options, fieldGroups, selectedFormData);
        } else {
          subscriber.sendData();
        }
      },
      saveData: (
        data: JsonDataItem | JsonData,
        returnResult: OdinDataSender<JsonDataWrapper>,
        options?: OdinDataRetrievalOptions
      ) => {
        if (!Array.isArray(data) && options?.customFieldType === 'ADDNEWMODAL' && options.fieldId) {
          returnResult.sendData({ data: [data], requestId: options.requestId });
        }
      },
    };
  }, [fieldGroups, selectedFormData]);

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between py-2">
        <h2 className="text-lg font-semibold">Field Details</h2>
        <CloseActiveItemButton onClick={onCloseFieldDetailsClick} />
      </div>
      {formSettings && (
        <DynamicForm
          ref={dynamicFormRef}
          dynamicFormId={formIdRef.current}
          settings={formSettings}
          onSubmit={() => {}}
          data={formData}
          dataRetrieval={dataRetrieval}
          formMode="all"
          onFieldChanged={handleChange}
        />
      )}
    </div>
  );
}

export default React.memo(FormBuilderFieldDetails);
